import { auth, db, storage } from "./firebase.js";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc, setDoc, getDoc, collection, addDoc, query, where,
  onSnapshot, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, listAll, getDownloadURL } from "firebase/storage";
import { installProbe } from "./probe.js";
import { getMessagingIfSupported, VAPID_KEY, app as fbApp } from "./firebase.js";
import { getToken, onMessage } from "firebase/messaging";
import { getInstallations, getId as getFid } from "firebase/installations";

const $ = (id) => document.getElementById(id);
const authStatus = (m) => ($("auth-status").textContent = m);

// ---- auth ----
$("btn-signup").onclick = () =>
  createUserWithEmailAndPassword(auth, $("email").value, $("password").value)
    .then(() => authStatus("signed up")).catch((e) => authStatus(e.code + " " + e.message));

$("btn-signin").onclick = () =>
  signInWithEmailAndPassword(auth, $("email").value, $("password").value)
    .then(() => authStatus("signed in")).catch((e) => authStatus(e.code + " " + e.message));

$("btn-google").onclick = () =>
  signInWithPopup(auth, new GoogleAuthProvider())
    .then(() => authStatus("google signed in")).catch((e) => authStatus(e.code + " " + e.message));

$("btn-signout").onclick = () => signOut(auth);

let unsubNotes = null;

onAuthStateChanged(auth, async (user) => {
  const inBoxes = ["profile-box", "notes-box", "files-box"];
  if (user) {
    $("signed-out").hidden = true;
    $("signed-in").hidden = false;
    $("whoami").textContent = `${user.email || user.uid} (${user.uid})`;
    inBoxes.forEach((b) => ($(b).hidden = false));
    await loadProfile(user);
    watchNotes(user);
    await loadFiles(user);
  } else {
    $("signed-out").hidden = false;
    $("signed-in").hidden = true;
    inBoxes.forEach((b) => ($(b).hidden = true));
    if (unsubNotes) unsubNotes();
  }
});

// ---- profile: users/{uid} ----
async function loadProfile(user) {
  const snap = await getDoc(doc(db, "users", user.uid));
  if (snap.exists()) {
    $("p-name").value = snap.data().displayName || "";
    $("p-bio").value = snap.data().bio || "";
  }
}
$("btn-save-profile").onclick = async () => {
  const u = auth.currentUser;
  await setDoc(doc(db, "users", u.uid), {
    displayName: $("p-name").value,
    bio: $("p-bio").value,
    owner: u.uid,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// ---- notes: notes/{id} ----
function watchNotes(user) {
  if (unsubNotes) unsubNotes();
  const q = query(collection(db, "notes"), where("owner", "==", user.uid));
  unsubNotes = onSnapshot(q, (qs) => {
    const ul = $("note-list");
    ul.innerHTML = "";
    qs.forEach((d) => {
      const li = document.createElement("li");
      li.textContent = `${d.id} — ${d.data().title} ${d.data().shared ? "[shared]" : ""} `;
      const del = document.createElement("button");
      del.textContent = "x";
      del.onclick = () => deleteDoc(doc(db, "notes", d.id));
      li.appendChild(del);
      ul.appendChild(li);
    });
  });
}
$("btn-add-note").onclick = async () => {
  const u = auth.currentUser;
  await addDoc(collection(db, "notes"), {
    owner: u.uid,
    title: $("n-title").value,
    body: $("n-body").value,
    shared: $("n-shared").checked,
    createdAt: serverTimestamp(),
  });
  $("n-title").value = $("n-body").value = "";
  $("n-shared").checked = false;
};

// ---- files: Storage users/{uid}/uploads/ ----
async function loadFiles(user) {
  const ul = $("file-list");
  ul.innerHTML = "";
  try {
    const res = await listAll(sRef(storage, `users/${user.uid}/uploads`));
    for (const item of res.items) {
      const li = document.createElement("li");
      const url = await getDownloadURL(item);
      li.innerHTML = `${item.name} — <a href="${url}" target="_blank">dl</a>`;
      ul.appendChild(li);
    }
  } catch (e) {
    ul.textContent = e.code || String(e);
  }
}
$("btn-upload").onclick = async () => {
  const u = auth.currentUser;
  const f = $("f-input").files[0];
  if (!f) return;
  await uploadBytes(sRef(storage, `users/${u.uid}/uploads/${f.name}`), f);
  await loadFiles(u);
};

// expose SDK internals for probing (test lab only)
window.probe = window.probe || {};
window.probe.auth = auth;
window.probe.db = db;
window.probe.storage = storage;
try {
  window.probe.emulatorConfig = {
    auth: auth.emulatorConfig || null,
    firestore: (db._settings && db._settings.host) || null,
  };
} catch (e) { window.probe.emulatorConfig = { err: String(e) }; }

// ---- Cloud Messaging: FCM token + FID ----
const fcmOut = (m) => ($("fcm-out").textContent = String(m));

$("btn-fcm-token").onclick = async () => {
  try {
    fcmOut("requesting notification permission...");
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return fcmOut("permission: " + perm);
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    await navigator.serviceWorker.ready;
    const messaging = await getMessagingIfSupported();
    if (!messaging) return fcmOut("messaging not supported here");
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: reg,
    });
    window.probe = window.probe || {};
    window.probe.fcmToken = token;
    fcmOut("FCM registration token:\n\n" + token);
    onMessage(messaging, (p) => console.log("[fb-lab] onMessage", JSON.stringify(p)));
  } catch (e) {
    fcmOut("error: " + (e && e.message ? e.message : String(e)));
  }
};

$("btn-fid").onclick = async () => {
  try {
    const fid = await getFid(getInstallations(fbApp));
    window.probe = window.probe || {};
    window.probe.fid = fid;
    fcmOut("Firebase installation ID (FID):\n\n" + fid);
  } catch (e) {
    fcmOut("error: " + (e && e.message ? e.message : String(e)));
  }
};

installProbe({ auth });
